/**
 * The browser tab shows what is going on: "● Working…" and an amber dot on the icon while a reply
 * is being written, "✓ Done" and a green dot when one finished while the tab was in the
 * background (until you look at it again).
 */
import { useEffect } from "react";
import { APP_NAME, ICON_SVG_BODY } from "./brand";
import { useLive } from "./live";

type Status = "idle" | "busy" | "done";

const APP = APP_NAME;
let pageTitle: string | null = null;
let status: Status = "idle";

const ICON_BODY = ICON_SVG_BODY;

function icon(dot: string | null): string {
  const badge = dot ? `<circle cx="25" cy="7" r="7" fill="${dot}" stroke="#fff" stroke-width="2"/>` : "";
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">${ICON_BODY}${badge}</svg>`)}`;
}

function render() {
  const base = pageTitle ? `${pageTitle} · ${APP}` : APP;
  document.title = status === "busy" ? `● Working… ${base}` : status === "done" ? `✓ Done · ${base}` : base;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/svg+xml";
  link.href = icon(status === "busy" ? "#f59e0b" : status === "done" ? "#22c55e" : null);
}

/** Sets the page's own title (e.g. the chat's) for as long as the component is shown. */
export function usePageTitle(title: string | null | undefined) {
  useEffect(() => {
    pageTitle = title || null;
    render();
    return () => {
      pageTitle = null;
      render();
    };
  }, [title]);
}

/** Mounted once (in the layout): follows all replies being written for this user. */
export function useTabStatus() {
  const busy = useLive((s) => Object.values(s.live).some((l) => l.status === "streaming"));
  useEffect(() => {
    if (busy) status = "busy";
    else if (status === "busy") status = document.hidden ? "done" : "idle";
    render();
  }, [busy]);
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && status === "done") {
        status = "idle";
        render();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
